import { FC, useEffect, useState } from 'react'
import { Alert, Button, Form, InputGroup, Tab, Tabs } from 'react-bootstrap'
import { Dialog } from 'primereact/dialog'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NavItemDialogProps, NavItemType } from './Navigation.d'
import { faPlusSquare } from '@fortawesome/free-regular-svg-icons'
import { useAppSelector } from '../../../../store/hooks'
import { Language } from '../../../../features/security/security.slice'
import { navNodeStyles, urlTypes } from '../../../common/Constants'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { v4 as uuidv4 } from 'uuid'
import { addNodeChild, editNodeChild } from './tree/treeHelper'

const NavItemDialog: FC<NavItemDialogProps> = ({
    item,
    formikProps,
    availablePages,
    button,
    setNodes,
    targetItemKey,
    className = '',
}) => {
    const [visible, setVisible] = useState(false)
    const [localItem, setLocalItem] = useState<NavItemType>()
    const [validationErrors, setValidationErrors] = useState<object>({})
    const initData = useAppSelector((state: any) => state.security.initData)

    const [firstInternalPageId, setFirstInternalPageId] = useState<
        number | null
    >(null)

    const { setFieldValue } = formikProps

    const isNew = item ? false : true

    useEffect(() => {
        const selectedVal = availablePages.filter((obj: any, index: number) => {
            return index === 0
        })

        setFirstInternalPageId(selectedVal[0]?.['id'] || '')
    }, [availablePages])

    const validateForm = () => {
        return new Promise((resolve, reject) => {
            setValidationErrors({})

            let errors = {}
            const divElem = document.getElementById(
                `form-navItem-${localItem?.key}`
            )
            const inputElements = divElem
                ? divElem.getElementsByTagName('input')
                : null

            for (var i = 0, n = inputElements.length; i < n; i = i + 1) {
                const isRequired = inputElements[i].required
                const value = inputElements[i].value
                const fieldName =
                    inputElements[i].getAttribute(
                        'data-validation-field-name'
                    ) || inputElements[i].name

                if (isRequired && !value && fieldName) {
                    errors = {
                        ...errors,
                        [fieldName]: `Field ${fieldName} is required!`,
                    }
                }
            }

            resolve(errors)
        })
    }

    const footerContent = (
        <div>
            <Button variant="outline-danger" onClick={() => setVisible(false)}>
                Cancel
            </Button>
            <Button
                variant="success"
                onClick={() => {
                    validateForm().then((errors: any) => {
                        setValidationErrors((prevState) => {
                            return {
                                ...prevState,
                                ...errors,
                            }
                        })
                        if (Object.keys(errors).length <= 0) {
                            setNodes((prevState) => {
                                let _val = prevState
                                if (isNew) {
                                    if (targetItemKey) {
                                        _val = addNodeChild(
                                            prevState,
                                            localItem,
                                            targetItemKey
                                        )
                                        setFieldValue('value', _val)
                                    } else {
                                        _val = [...prevState, localItem]
                                        setFieldValue('value', _val)
                                    }
                                } else {
                                    _val = editNodeChild(prevState, localItem)
                                    setFieldValue('value', _val)
                                }

                                return _val
                            })

                            setVisible(false)
                        }
                    })
                }}
                className="p-button-text"
            >
                Done
            </Button>
        </div>
    )

    return (
        <>
            <Button
                variant={button?.variant || 'primary'}
                className={`border position-relative ${className || ''}`}
                size="sm"
                onClick={() => {
                    setVisible(true)
                }}
            >
                {button?.label || (isNew ? 'New Item' : 'Edit')}{' '}
                <FontAwesomeIcon icon={isNew ? faPlusSquare : faEdit} />
            </Button>
            <Dialog
                header={isNew ? <>Create New Item</> : <>Edit Item</>}
                visible={visible}
                breakpoints={{ '960px': '80vw', '640px': '90vw' }}
                style={{ width: '50vw' }}
                onHide={() => {
                    setVisible(false)
                }}
                closable={true}
                onShow={() => {
                    const selectedVal = availablePages.filter(
                        (obj: any, index: number) => {
                            return index === 0
                        }
                    )
                    const newKey = uuidv4()

                    setLocalItem(
                        item || {
                            key: newKey,
                            label: newKey.toString().toUpperCase(),
                            path: '/',
                            pathId: selectedVal[0]?.['id'] || '',
                        }
                    )
                }}
            >
                {localItem && (
                    <div
                        className="bg-gray p-3 shadow border mb-4"
                        id={`form-navItem-${localItem.key}`}
                    >
                        {Object.keys(validationErrors).length > 0 ? (
                            <Alert variant="danger">
                                {Object.values(validationErrors)?.map(
                                    (error, index) => {
                                        return <div key={index}>{error}</div>
                                    }
                                )}
                            </Alert>
                        ) : (
                            <></>
                        )}
                        <Form.Group className="mb-3" controlId="NavItem_1">
                            <Tabs
                                id={`_navItem${localItem.key}_fieldTabs`}
                                className="mb-0"
                            >
                                {Object.keys(initData.languages.data).map(
                                    (locale) => {
                                        const lang: Language =
                                            initData.languages.data[locale]
                                        const title = localItem.translations?.[
                                            locale
                                        ]?.hasOwnProperty(lang.name)
                                            ? localItem.translations[locale][
                                                  lang.name
                                              ]
                                            : lang.name

                                        const navItemLabelProp = `navItem_${localItem.key}_label_${lang.id}`

                                        return (
                                            <Tab
                                                eventKey={locale}
                                                title={title}
                                                key={locale}
                                                className="fade border-start border-end border-bottom mb-3 p-2"
                                            >
                                                <Form.Control
                                                    type="text"
                                                    name={navItemLabelProp}
                                                    value={
                                                        localItem
                                                            .translations?.[
                                                            locale
                                                        ]?.[localItem.label] ||
                                                        ''
                                                    }
                                                    onChange={(e) => {
                                                        setLocalItem({
                                                            ...localItem,
                                                            translations: {
                                                                ...(localItem.translations ||
                                                                    {}),
                                                                [locale]: {
                                                                    ...(localItem
                                                                        .translations?.[
                                                                        locale
                                                                    ] || {}),
                                                                    [localItem.label]:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            },
                                                        })
                                                    }}
                                                    placeholder={`Entere label (${lang.name})`}
                                                    required
                                                    data-validation-field-name={`Label (${lang.name})`}
                                                />
                                            </Tab>
                                        )
                                    }
                                )}
                            </Tabs>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="navItem_path">
                            <InputGroup className="mb-3">
                                <Form.Select
                                    value={localItem.pathLocation}
                                    size="sm"
                                    style={{
                                        width: '33%',
                                    }}
                                    onChange={(e) => {
                                        setLocalItem({
                                            ...localItem,
                                            pathLocation: e.target.value,
                                            pathId: firstInternalPageId,
                                        })
                                    }}
                                    required
                                >
                                    {urlTypes.map((choice: any) => (
                                        <option
                                            key={choice.id}
                                            value={choice.id}
                                        >
                                            {choice.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                {localItem.pathLocation !== 'external' ? (
                                    <Form.Select
                                        value={localItem.pathId}
                                        size="sm"
                                        style={{
                                            width: '33%',
                                        }}
                                        onChange={(e) => {
                                            const pathId = parseInt(
                                                e.target.value
                                            )
                                            const selectedVal =
                                                availablePages.filter(
                                                    (obj: any) => {
                                                        return (
                                                            parseInt(obj.id) ===
                                                            pathId
                                                        )
                                                    }
                                                )

                                            const url =
                                                selectedVal.length === 1
                                                    ? selectedVal[0]['code']
                                                    : ''

                                            setLocalItem({
                                                ...localItem,
                                                path: '/' + url,
                                                pathId,
                                            })
                                        }}
                                        required
                                        data-validation-field-name="Path"
                                    >
                                        {availablePages.map((choice: any) => (
                                            <option
                                                key={choice.id}
                                                value={choice.id}
                                            >
                                                {choice.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                ) : (
                                    <Form.Control
                                        value={localItem.path}
                                        size="sm"
                                        style={{
                                            width: '33%',
                                        }}
                                        onChange={(e) => {
                                            setLocalItem({
                                                ...localItem,
                                                path: e.target.value,
                                            })
                                        }}
                                        required
                                        data-validation-field-name="Path"
                                    />
                                )}
                                <Form.Select
                                    value={localItem.nodeStyle || ''}
                                    size="sm"
                                    style={{
                                        width: '33%',
                                    }}
                                    onChange={(e) => {
                                        setLocalItem({
                                            ...localItem,
                                            nodeStyle: (e.target.value ||
                                                'link') as 'link' | 'button',
                                        })
                                    }}
                                    required
                                >
                                    {navNodeStyles.map((choice: any) => (
                                        <option
                                            key={choice.id}
                                            value={choice.id}
                                        >
                                            {choice.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </InputGroup>
                        </Form.Group>
                    </div>
                )}
                <div className="p-dialog-footer p-0 pt-3">{footerContent}</div>
            </Dialog>
        </>
    )
}

export default NavItemDialog
